import { Component } from '@angular/core';

type PolicyCategoryId = 'all' | 'privacy' | 'candidates' | 'companies' | 'legal';
type PolicyItemCategoryId = Exclude<PolicyCategoryId, 'all'>;

interface PolicyCategory {
  id: PolicyCategoryId;
  labelKey: string;
}

interface PolicyItem {
  id: string;
  iconImg: string;
  category: PolicyItemCategoryId;
  titleKey: string;
  summaryKey: string;
  points: string[];
  searchKeywords: string;
}

@Component({
  selector: 'app-policies',
  templateUrl: './policies.component.html',
  styleUrl: './policies.component.scss'
})
export class PoliciesComponent {
  searchTerm = '';
  activeCategory: PolicyCategoryId = 'all';

  openedPolicyIds = new Set<string>(['privacy', 'cvData']);

  categories: PolicyCategory[] = [
    { id: 'all', labelKey: 'atw.policies.filters.all' },
    { id: 'privacy', labelKey: 'atw.policies.filters.privacy' },
    { id: 'candidates', labelKey: 'atw.policies.filters.candidates' },
    { id: 'companies', labelKey: 'atw.policies.filters.companies' },
    { id: 'legal', labelKey: 'atw.policies.filters.legal' }
  ];

  policies: PolicyItem[] = [
    {
      id: 'privacy',
      category: 'privacy',
      iconImg: 'assets/as/37.webp',
      titleKey: 'atw.policies.sections.privacy.title',
      summaryKey: 'atw.policies.sections.privacy.summary',
      points: [
        'atw.policies.sections.privacy.point1',
        'atw.policies.sections.privacy.point2',
        'atw.policies.sections.privacy.point3',
        'atw.policies.sections.privacy.point4'
      ],
      searchKeywords: 'privacy data personal information protection سياسة الخصوصية البيانات الشخصية حماية المعلومات'
    },
    {
      id: 'cvData',
      category: 'candidates',
      iconImg: 'assets/as/12.webp',
      titleKey: 'atw.policies.sections.cvData.title',
      summaryKey: 'atw.policies.sections.cvData.summary',
      points: [
        'atw.policies.sections.cvData.point1',
        'atw.policies.sections.cvData.point2',
        'atw.policies.sections.cvData.point3',
        'atw.policies.sections.cvData.point4'
      ],
      searchKeywords: 'cv resume upload candidate profile job matching السيرة الذاتية رفع cv المرشح الوظائف'
    },
    {
      id: 'candidateTerms',
      category: 'candidates',
      iconImg: 'assets/as/16.webp',
      titleKey: 'atw.policies.sections.candidateTerms.title',
      summaryKey: 'atw.policies.sections.candidateTerms.summary',
      points: [
        'atw.policies.sections.candidateTerms.point1',
        'atw.policies.sections.candidateTerms.point2',
        'atw.policies.sections.candidateTerms.point3',
        'atw.policies.sections.candidateTerms.point4'
      ],
      searchKeywords: 'candidate terms application job responsibility شروط المرشح التقديم الوظائف المسؤولية'
    },
    {
      id: 'companyTerms',
      category: 'companies',
      iconImg: 'assets/as/9.webp',
      titleKey: 'atw.policies.sections.companyTerms.title',
      summaryKey: 'atw.policies.sections.companyTerms.summary',
      points: [
        'atw.policies.sections.companyTerms.point1',
        'atw.policies.sections.companyTerms.point2',
        'atw.policies.sections.companyTerms.point3',
        'atw.policies.sections.companyTerms.point4'
      ],
      searchKeywords: 'companies employers hiring jobs accuracy partners الشركات أصحاب الأعمال التوظيف الوظائف'
    },
    {
      id: 'communication',
      category: 'privacy',
      iconImg: 'assets/as/38.webp',
      titleKey: 'atw.policies.sections.communication.title',
      summaryKey: 'atw.policies.sections.communication.summary',
      points: [
        'atw.policies.sections.communication.point1',
        'atw.policies.sections.communication.point2',
        'atw.policies.sections.communication.point3'
      ],
      searchKeywords: 'communication email phone whatsapp updates التواصل البريد الهاتف واتساب التحديثات'
    },
    {
      id: 'thirdParty',
      category: 'legal',
      iconImg: 'assets/as/1.webp',
      titleKey: 'atw.policies.sections.thirdParty.title',
      summaryKey: 'atw.policies.sections.thirdParty.summary',
      points: [
        'atw.policies.sections.thirdParty.point1',
        'atw.policies.sections.thirdParty.point2',
        'atw.policies.sections.thirdParty.point3'
      ],
      searchKeywords: 'third party external links websites partners أطراف خارجية روابط خارجية شركاء مواقع'
    },
    {
      id: 'disclaimer',
      category: 'legal',
      iconImg: 'assets/as/36.webp',
      titleKey: 'atw.policies.sections.disclaimer.title',
      summaryKey: 'atw.policies.sections.disclaimer.summary',
      points: [
        'atw.policies.sections.disclaimer.point1',
        'atw.policies.sections.disclaimer.point2',
        'atw.policies.sections.disclaimer.point3',
        'atw.policies.sections.disclaimer.point4'
      ],
      searchKeywords: 'disclaimer guarantee acceptance visa job offer تنبيه قانوني ضمان قبول تأشيرة عرض عمل'
    }
  ];

  get filteredPolicies(): PolicyItem[] {
    const term = this.searchTerm.trim().toLowerCase();

    return this.policies.filter((policy) => {
      const matchesCategory =
        this.activeCategory === 'all' || policy.category === this.activeCategory;

      const matchesSearch =
        !term ||
        policy.id.toLowerCase().includes(term) ||
        policy.searchKeywords.toLowerCase().includes(term);

      return matchesCategory && matchesSearch;
    });
  }

  onSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
  }

  setCategory(categoryId: PolicyCategoryId): void {
    this.activeCategory = categoryId;
  }

  togglePolicy(policyId: string): void {
    if (this.openedPolicyIds.has(policyId)) {
      this.openedPolicyIds.delete(policyId);
      return;
    }

    this.openedPolicyIds.add(policyId);
  }

  isOpen(policyId: string): boolean {
    return this.openedPolicyIds.has(policyId);
  }

  openAll(): void {
    this.filteredPolicies.forEach((policy) => {
      this.openedPolicyIds.add(policy.id);
    });
  }

  closeAll(): void {
    this.openedPolicyIds.clear();
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.activeCategory = 'all';
    this.openedPolicyIds = new Set<string>(['privacy', 'cvData']);
  }

  trackByCategory(index: number, category: PolicyCategory): string {
    return category.id;
  }

  trackByPolicy(index: number, policy: PolicyItem): string {
    return policy.id;
  }

  trackByPoint(index: number, pointKey: string): string {
    return pointKey;
  }
}